/**
 * Reciprocal hreflang for the two public sites.
 *
 * Both builds emit the same cluster, on the page's own path:
 *   `en`        → flogpt.com   (the page is English, not a region)
 *   `en-IN`     → flogpt.in
 *   `x-default` → flogpt.com   (fallback when the visitor's locale matches neither)
 *
 * `x-default` is not a language code, so it does not replace `en`. Google
 * requires the cluster to match on every alternate, including a self-reference.
 *
 * Blog posts are not in this set. They call `applyReciprocalHreflang` only
 * for slugs in `INDIA_BLOG_SLUGS`, which is empty while flogpt.in has no posts.
 */

const MARK = "data-flogpt-hreflang";

export const canonicalPathname = (canonical: string): string => {
    let path = canonical;
    if (/^https?:\/\//i.test(canonical)) {
        try {
            path = new URL(canonical).pathname || "/";
        } catch {
            path = "/";
        }
    }
    if (!path.startsWith("/")) path = `/${path}`;
    const bare = path.split("?")[0].split("#")[0];
    if (bare !== "/" && bare.endsWith("/")) return bare.slice(0, -1);
    return bare || "/";
};

export const hreflangHref = (origin: string, path: string): string => {
    const normalised = canonicalPathname(path);
    return normalised === "/" ? `${origin}/` : `${origin}${normalised}`;
};

/** Insert the reciprocal pair. Replaces any pair this module inserted earlier. */
export const applyReciprocalHreflang = (path: string): HTMLLinkElement[] => {
    clearOwnedHreflang();
    return [
        ["en", "https://flogpt.com"],
        ["en-IN", "https://flogpt.in"],
        ["x-default", "https://flogpt.com"],
    ].map(([hreflang, origin]) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = hreflang;
        link.href = hreflangHref(origin, path);
        link.setAttribute(MARK, "1");
        document.head.appendChild(link);
        return link;
    });
};

export const clearOwnedHreflang = (): void => {
    document.querySelectorAll(`link[rel="alternate"][${MARK}]`).forEach((el) => el.remove());
};
